import {
  useLayoutEffect,
  useMemo,
  useRef,
} from 'react';

import {
  Box,
  Paper,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';

import Chart, { useChart } from 'src/components/chart';
import { useCurrentWorkspace } from 'src/context/reducers/app-settings';
import { useLocales } from 'src/locales';

import {
  CHART_HEIGHT,
  formatExploreLabels,
} from './constants';

interface ExploreChartProps {
  toggle: any;
  filterStore: any;
  hiddenMetrics?: string[];
  showLabels?: boolean;
  matchScale?: boolean;
  onChartMounted?: (chartInstance: any) => void;
}

type ChartSeries = {
  name: string;
  key: string;
  type: 'bar' | 'line';
  data: number[];
  color: string;
  format?: string;
};

type ComparisonValue = {
  metricKey: string;
  color: string;
  value: number | null;
};

type AxisKey = 'traffic' | 'ctr' | 'cost' | 'cpc';

type MetricChartType = 'bar' | 'line';


const PAID_KPI_COLOR = '#DAA335';
const ORGANIC_KPI_COLOR = '#18A155';
const TRAFFIC_KPI_COLOR = '#637381';

/**
 * Couleurs produit pour Metric Evolution.
 *
 * Priorité :
 * 1. Organic => vert
 * 2. Paid / Ads / Search / Shopping / PMax => jaune
 * 3. Traffic global / Brand Traffic => gris
 * 4. fallback => couleur du catalogue KPI
 */
export const getMetricEvolutionColor = (
  metricKey: string,
  metricTitle = '',
  fallbackColor = '#637381'
): string => {
  const key = metricKey.toLowerCase();
  const title = metricTitle.toLowerCase();
  const normalized = `${key} ${title}`;

  const isOrganic =
    normalized.includes('organic');

  if (isOrganic) {
    return ORGANIC_KPI_COLOR;
  }

  const isPaid =
    normalized.includes('paid') ||
    normalized.includes('pmax') ||
    key.startsWith('ad_') ||
    key.startsWith('search_') ||
    key.startsWith('shopping_') ||
    key === 'ad_cost' ||
    key === 'cpc' ||
    title.includes('ad brand traffic') ||
    title.includes('paid cpc') ||
    title.includes('paid ctr') ||
    title.includes('paid traffic');

  if (isPaid) {
    return PAID_KPI_COLOR;
  }

  const isTraffic =
    key === 'total_brand_traffic' ||
    key === 'brand_traffic' ||
    title === 'traffic' ||
    title === 'brand traffic';

  if (isTraffic) {
    return TRAFFIC_KPI_COLOR;
  }

  return fallbackColor || TRAFFIC_KPI_COLOR;
};

/**
 * Détermine automatiquement le type de graphique selon la métrique.
 *
 * Bar :
 * - toutes les métriques Ad Cost ;
 * - toutes les métriques Traffic ;
 *
 * Line :
 * - toutes les métriques CTR ;
 * - toutes les métriques CPC ;
 * - Brand Searches ;
 * - Brand Traffic.
 *
 * Attention :
 * Brand Traffic constitue l'exception à la règle générale "Traffic = bar".
 */
const getMetricChartType = (
  metricKey: string,
  metricTitle = ''
): MetricChartType => {
  const normalizedKey = metricKey.toLowerCase();
  const normalizedTitle = metricTitle.toLowerCase();

  /**
   * Métriques explicitement rendues en ligne.
   * On vérifie ces exceptions avant la règle générique "traffic".
   */
  const isBrandSearches =
    normalizedKey === 'brand_searches' ||
    normalizedTitle === 'brand searches';

  const isBrandTraffic =
    normalizedKey === 'total_brand_traffic' ||
    normalizedKey === 'brand_traffic' ||
    normalizedTitle === 'brand traffic';

  const isCtr =
    normalizedKey.includes('ctr') ||
    normalizedTitle.includes('ctr');

  const isCpc =
    normalizedKey.includes('cpc') ||
    normalizedTitle.includes('cpc');

  if (
    isBrandSearches ||
    isBrandTraffic ||
    isCtr ||
    isCpc
  ) {
    return 'line';
  }

  /**
   * Toutes les métriques Ad Cost sont des barres.
   */
  const isAdCost =
    normalizedKey === 'ad_cost' ||
    normalizedKey.endsWith('_ad_cost') ||
    normalizedKey.includes('ad_cost') ||
    normalizedTitle.includes('ad cost');

  /**
   * Toutes les métriques Traffic sont des barres,
   * sauf Brand Traffic, déjà traité au-dessus.
   */
  const isTraffic =
    normalizedKey.includes('traffic') ||
    normalizedTitle.includes('traffic');

  if (isAdCost || isTraffic) {
    return 'bar';
  }

  /**
   * Fallback sécurisé :
   * une métrique non catégorisée reste une ligne.
   */
  return 'line';
};

/**
 * Nombre d’intervalles utilisé pour les axes.
 *
 * 5 intervalles produisent 6 labels :
 * 0, 1, 2, 3, 4, 5.
 */
const MATCH_SCALE_TICK_AMOUNT = 5;

/**
 * Métriques de coût global.
 *
 * Les autres métriques en devise sont considérées comme des CPC.
 */
const COST_KEYS = new Set([
  'ad_cost',
  'search_ad_cost',
  'shopping_ad_cost',
  'pmax_ad_cost',
]);

/**
 * Arrondit une valeur vers un palier lisible.
 *
 * Exemples :
 * 1 240 devient 1 500
 * 6 200 devient 7 000
 */
const roundUpToNiceInterval = (value: number): number => {
  if (value <= 0) return 0.5;

  const magnitude = Math.pow(
    10,
    Math.floor(Math.log10(value))
  );

  const normalized = value / magnitude;

  let nice: number;

  if (normalized <= 1.2) nice = 1.2;
  else if (normalized <= 1.5) nice = 1.5;
  else if (normalized <= 2) nice = 2;
  else if (normalized <= 3) nice = 3;
  else if (normalized <= 5) nice = 5;
  else if (normalized <= 7) nice = 7;
  else nice = 10;

  return nice * magnitude;
};

/**
 * Crée le formatter d’une métrique à partir du catalogue KPI.
 *
 * Exemples de formats :
 * - currency:2
 * - percentage:1
 * - number:0
 * - float:2
 */
const makeFormatter = (
  format: string | undefined,
  locale: string,
  currency?: string
) => {
  const [formatter, numberOfDecimals] =
    (format ?? '').split(':');

  const decimals =
    parseInt(numberOfDecimals, 10) || 0;

  if (formatter === 'currency') {
    return (value = 0) =>
      Intl.NumberFormat(locale, {
        style: 'currency',
        currency: currency || 'USD',
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
        notation:
          Math.abs(value) > 1000
            ? 'compact'
            : 'standard',
      }).format(value ?? 0);
  }

  if (formatter === 'percentage') {
    return (value = 0) =>
      `${(value ?? 0).toFixed(decimals)}%`;
  }

  if (
    formatter === 'number' ||
    formatter === 'float'
  ) {
    return (value = 0) =>
      (value ?? 0).toLocaleString(locale, {
        maximumFractionDigits: decimals,
      });
  }

  return (value: number) => `${value}`;
};

const makeDisplayFormatter = (
  format: string | undefined,
  locale: string,
  currency?: string
) => {
  const [formatter] = (format ?? '').split(':');

  if (formatter === 'currency') {
    return (value = 0) =>
      Intl.NumberFormat(locale, {
        style: 'currency',
        currency: currency || 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
        notation:
          Math.abs(value) > 1000
            ? 'compact'
            : 'standard',
      }).format(value ?? 0);
  }

  if (formatter === 'percentage') {
    return (value = 0) =>
      `${Math.round(value ?? 0)}%`;
  }

  if (
    formatter === 'number' ||
    formatter === 'float'
  ) {
    return (value = 0) =>
      Math.round(value ?? 0).toLocaleString(locale);
  }

  return (value: number) =>
    Math.round(value).toLocaleString(locale);
};

/**
 * Détermine si une métrique est un pourcentage.
 */
const isPercentFormat = (
  format: string | undefined
) =>
  (format ?? '').split(':')[0] ===
  'percentage';

/**
 * Associe une série à sa famille d’axe Y.
 */
const axisOf = (
  serie: Pick<ChartSeries, 'key' | 'format'>
): AxisKey => {
  if (isPercentFormat(serie.format)) {
    return 'ctr';
  }

  const formatter =
    (serie.format ?? '').split(':')[0];

  if (formatter === 'currency') {
    return COST_KEYS.has(serie.key)
      ? 'cost'
      : 'cpc';
  }

  return 'traffic';
};

export default function ExploreChart({
  toggle,
  filterStore,
  hiddenMetrics = [],
  showLabels = false,
  matchScale = false,
  onChartMounted,
}: ExploreChartProps) {
  const { currentLang } = useLocales();

  const locale = currentLang.value;

  const workspace: any =
    useCurrentWorkspace();

  const currency = workspace?.currency;

  const loading =
    toggle.isLoadingStats ||
    toggle.isLoadingCompareStats ||
    !toggle.statsData;

  /**
   * Conteneur du graphique ApexCharts.
   *
   * Il permet de retrouver les vrais labels X générés dans le SVG.
   */
  const chartContainerRef =
    useRef<HTMLDivElement | null>(null);

  /**
   * Zone contenant les valeurs de comparaison.
   */
  const comparisonContainerRef =
    useRef<HTMLDivElement | null>(null);

  /**
   * Label de la période comparée, affiché à gauche des valeurs
   * d'évolution comme dans la maquette Figma.
   */
  const comparisonPeriodRef =
    useRef<HTMLDivElement | null>(null);

  /**
   * Références vers chaque bloc de comparaison.
   *
   * Les positions sont appliquées directement dans le DOM,
   * sans setState, pour éviter les boucles de rendu ApexCharts.
   */
  const comparisonItemRefs =
    useRef<Array<HTMLDivElement | null>>([]);


  /**
   * Construit toutes les séries en conservant l’index initial.
   *
   * Important :
   * on détermine le type avant de filtrer les métriques masquées.
   * Cela évite qu’une série change de type lorsqu’une métrique
   * précédente est masquée.
   *
   * Metric 1 => bar
   * Metric 2 => line
   * Metric 3 => line
   */
  const series = useMemo<ChartSeries[]>(() => {
    const timeSeries =
      toggle.statsData?.time_series_data ?? [];

    return toggle.kpiToggle
      .map(
        (
          kpi: string,
          index: number
        ): ChartSeries | null => {
          const stat = toggle.stats.find(
            (item: any) => item.key === kpi
          );

          if (!stat) return null;

          const isPercentage =
            isPercentFormat(stat.format);

          const data = timeSeries.map(
            (item: any) => {
              const value = item[kpi] ?? 0;

              return isPercentage
                ? Math.min(value, 100)
                : value;
            }
          );

          return {
            name: stat.title,
            key: kpi,
            type: getMetricChartType(kpi, stat.title),
            data,
            color: getMetricEvolutionColor(kpi, stat.title, stat.color),
            format: stat.format as
              | string
              | undefined,
          };
        }
      )
      .filter(
        (serie: any): serie is ChartSeries =>
          Boolean(serie) &&
          !hiddenMetrics.includes(serie.key)
      );
  }, [
    toggle.kpiToggle,
    toggle.stats,
    toggle.statsData,
    hiddenMetrics,
  ]);

  /**
   * Labels de l’axe X.
   */
  const labels = formatExploreLabels(
    toggle.statsData?.time_series_data ?? [],
    filterStore.analysisChartTimestep,
    locale
  );

  /**
 * Calcule l'évolution de chaque métrique pour chaque bucket.
 *
 * Les buckets sont associés par index :
 * - premier bucket actuel ↔ premier bucket comparé ;
 * - deuxième bucket actuel ↔ deuxième bucket comparé ;
 * etc.
 */
  const comparisonValues = useMemo<ComparisonValue[][]>(() => {
    const comparisonEnabled =
      filterStore.analysisChartCompareMode !== 'none';

    if (!comparisonEnabled) {
      return [];
    }

    const currentTimeSeries =
      toggle.statsData?.time_series_data ?? [];

    const compareTimeSeries =
      toggle.compareStatsData?.time_series_data ?? [];

    if (
      currentTimeSeries.length === 0 ||
      compareTimeSeries.length === 0
    ) {
      return [];
    }

    return currentTimeSeries.map(
      (currentBucket: any, bucketIndex: number) => {
        const compareBucket =
          compareTimeSeries[bucketIndex];

        return series.map((metric) => {
          const currentValue =
            Number(currentBucket?.[metric.key]) || 0;

          const compareValue =
            Number(compareBucket?.[metric.key]) || 0;

          let evolution: number | null = null;

          if (compareValue !== 0) {
            evolution =
              ((currentValue - compareValue) /
                Math.abs(compareValue)) *
              100;
          } else if (currentValue === 0) {
            evolution = 0;
          }

          return {
            metricKey: metric.key,
            color: metric.color,
            value: evolution,
          };
        });
      }
    );
  }, [
    filterStore.analysisChartCompareMode,
    toggle.statsData,
    toggle.compareStatsData,
    series,
  ]);

  /**
 * Formate une évolution en pourcentage.
 */
  const formatComparisonValue = (
    value: number | null
  ): string => {
    if (value === null) {
      return '—';
    }

    if (value > 999) {
      return '>999%';
    }

    if (value < -999) {
      return '<-999%';
    }

    return `${value > 0 ? '+' : ''}${Math.round(value)}%`;
  };

  /**
   * Formate la période comparée dans le style Figma :
   *
   * 07 Apr - 21 Apr
   * 2025
   *
   * Le format reste volontairement en anglais abrégé afin de respecter
   * la règle produit "DD Mon YYYY".
   */
  const comparisonPeriodLabel = useMemo(() => {
    if (
      filterStore.analysisChartCompareMode === 'none' ||
      !filterStore.analysisChartCompareStart ||
      !filterStore.analysisChartCompareEnd
    ) {
      return '';
    }

    const startDate = new Date(
      filterStore.analysisChartCompareStart
    );
    const endDate = new Date(
      filterStore.analysisChartCompareEnd
    );

    if (
      Number.isNaN(startDate.getTime()) ||
      Number.isNaN(endDate.getTime())
    ) {
      return '';
    }

    const getDateParts = (date: Date) => {
      const parts = new Intl.DateTimeFormat('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        timeZone: 'UTC',
      }).formatToParts(date);

      return {
        day:
          parts.find((part) => part.type === 'day')
            ?.value ?? '',
        month:
          parts.find((part) => part.type === 'month')
            ?.value ?? '',
        year:
          parts.find((part) => part.type === 'year')
            ?.value ?? '',
      };
    };

    const start = getDateParts(startDate);
    const end = getDateParts(endDate);

    if (start.year === end.year) {
      return `${start.day} ${start.month} - ${end.day} ${end.month}\n${start.year}`;
    }

    return `${start.day} ${start.month} ${start.year} -\n${end.day} ${end.month} ${end.year}`;
  }, [
    filterStore.analysisChartCompareMode,
    filterStore.analysisChartCompareStart,
    filterStore.analysisChartCompareEnd,
  ]);

  const showComparison =
    filterStore.analysisChartCompareMode !== 'none' &&
    comparisonValues.length > 0;


  /**
* Aligne chaque bloc de comparaison avec le centre réel
* du label de date produit par ApexCharts.
*
* On ne calcule aucune marge approximative :
* les coordonnées viennent directement du SVG rendu.
*/
  useLayoutEffect(() => {
    if (!showComparison) {
      return undefined;
    }

    const chartContainer =
      chartContainerRef.current;

    const comparisonContainer =
      comparisonContainerRef.current;

    if (
      !chartContainer ||
      !comparisonContainer
    ) {
      return undefined;
    }

    let animationFrameId: number | null = null;

    const updateComparisonPositions = () => {
      if (animationFrameId !== null) {
        cancelAnimationFrame(animationFrameId);
      }

      animationFrameId =
        requestAnimationFrame(() => {
          const currentChartContainer =
            chartContainerRef.current;

          const currentComparisonContainer =
            comparisonContainerRef.current;

          if (
            !currentChartContainer ||
            !currentComparisonContainer
          ) {
            return;
          }

          /**
           * ApexCharts ajoute cette classe aux textes de l’axe X.
           */
          const xAxisLabels =
            currentChartContainer.querySelectorAll<SVGTextElement>(
              '.apexcharts-xaxis-texts-g .apexcharts-xaxis-label'
            );

          const comparisonRect =
            currentComparisonContainer.getBoundingClientRect();

          /**
           * Le label de période occupe visuellement le "slot" situé
           * juste à gauche du premier bucket, comme dans la maquette.
           */
          const comparisonPeriod =
            comparisonPeriodRef.current;

          if (
            comparisonPeriod &&
            xAxisLabels.length > 0
          ) {
            const firstLabelRect =
              xAxisLabels[0].getBoundingClientRect();

            const firstCenterX =
              firstLabelRect.left +
              firstLabelRect.width / 2 -
              comparisonRect.left;

            let bucketSpacing = 104;

            if (xAxisLabels.length > 1) {
              const secondLabelRect =
                xAxisLabels[1].getBoundingClientRect();

              const secondCenterX =
                secondLabelRect.left +
                secondLabelRect.width / 2 -
                comparisonRect.left;

              bucketSpacing =
                Math.abs(
                  secondCenterX -
                  firstCenterX
                ) || bucketSpacing;
            }

            const periodCenterX =
              Math.max(
                52,
                firstCenterX - bucketSpacing
              );

            comparisonPeriod.style.left =
              `${periodCenterX}px`;
          }

          comparisonItemRefs.current.forEach(
            (comparisonItem, index) => {
              const xAxisLabel =
                xAxisLabels[index];

              if (
                !comparisonItem ||
                !xAxisLabel
              ) {
                return;
              }

              const labelRect =
                xAxisLabel.getBoundingClientRect();

              /**
               * Centre du label par rapport à la zone
               * de comparaison.
               */
              const labelCenterX =
                labelRect.left +
                labelRect.width / 2 -
                comparisonRect.left;

              comparisonItem.style.left =
                `${labelCenterX}px`;
            }
          );
        });
    };

    /**
     * Premier positionnement après que le SVG ApexCharts
     * a terminé son rendu.
     */
    updateComparisonPositions();

    /**
     * Repositionnement lorsque la modal ou le graphique
     * change de largeur.
     */
    const resizeObserver =
      new ResizeObserver(() => {
        updateComparisonPositions();
      });

    resizeObserver.observe(chartContainer);
    resizeObserver.observe(comparisonContainer);

    window.addEventListener(
      'resize',
      updateComparisonPositions
    );

    return () => {
      if (animationFrameId !== null) {
        cancelAnimationFrame(animationFrameId);
      }

      resizeObserver.disconnect();

      window.removeEventListener(
        'resize',
        updateComparisonPositions
      );
    };
  }, [
    showComparison,
    labels,
    comparisonValues,
    series,
    comparisonPeriodLabel,
  ]);

  /**
   * Calcule un maximum propre pour un groupe de séries.
   *
   * Utilisé quand Match Scale est désactivé.
   */
  const maxOf = (
    group: ChartSeries[]
  ): number => {
    const values = group
      .flatMap((serie) => serie.data)
      .filter(
        (value) =>
          value != null &&
          Number.isFinite(value)
      );

    const max = values.length
      ? Math.max(...values)
      : 0;

    return max <= 0
      ? 0.5
      : roundUpToNiceInterval(max * 1.3);
  };

  /**
   * Calcule le plafond partagé utilisé lorsque Match Scale est actif.

   * Exemple :
   * valeur max réelle = 6 200
   * step brut = 6 200 / 5 = 1 240
   * step propre = 1 500
   * plafond final = 1 500 × 5 = 7 500
   */
  const sharedMax = useMemo(() => {
    const scalableSeries = series;

    const values = scalableSeries
      .flatMap((serie) => serie.data)
      .filter(
        (
          value
        ): value is number =>
          typeof value === 'number' &&
          Number.isFinite(value) &&
          value >= 0
      );

    const rawMax = values.length
      ? Math.max(...values)
      : 0;

    if (rawMax <= 0) {
      return MATCH_SCALE_TICK_AMOUNT;
    }

    const rawStep =
      rawMax / MATCH_SCALE_TICK_AMOUNT;

    const niceStep =
      roundUpToNiceInterval(rawStep);

    return (
      niceStep *
      MATCH_SCALE_TICK_AMOUNT
    );
  }, [series]);

  /**
   * Ordre et position des familles d’axes.
   *
   * Traffic et Cost à gauche.
   * CTR et CPC à droite.
   */
  const AXIS_ORDER: Array<{
    key: AxisKey;
    opposite: boolean;
  }> = [
      {
        key: 'traffic',
        opposite: false,
      },
      {
        key: 'cost',
        opposite: false,
      },
      {
        key: 'ctr',
        opposite: true,
      },
      {
        key: 'cpc',
        opposite: true,
      },
    ];

  /**
   * Construction des axes Y.
   *
   * Match Scale OFF :
   * chaque famille de métriques utilise son propre maximum.
   *
   * Match Scale ON :
   * traffic, cost et cpc utilisent le même maximum numérique.
   *
   * Le CTR conserve toujours son échelle 0–100 %.
   */
  const yaxis = AXIS_ORDER
    .map(({ key, opposite }) => ({
      key,
      opposite,
      group: series.filter(
        (serie) => axisOf(serie) === key
      ),
    }))
    .filter(
      ({ group }) => group.length > 0
    )
    .map(
      ({
        key,
        opposite,
        group,
      }) => {
        const formatter = makeDisplayFormatter(
          group[0]?.format,
          locale,
          currency
        );

        const axisMax =
          matchScale
            ? sharedMax
            : key === 'ctr'
              ? 100
              : maxOf(group);

        return {
          seriesName: group.map(
            (serie) => serie.name
          ),

          min: 0,
          max: axisMax,

          tickAmount:
            MATCH_SCALE_TICK_AMOUNT,

          forceNiceScale: false,

          show: true,
          opposite,

          // 👇 AJOUTER ÇA
          title: {
            text: group
              .map((serie) => serie.name)
              .join(' / '),
            style: {
              color: '#637381',
              fontSize: '11px',
              fontWeight: 500,
              fontFamily: 'Public Sans, sans-serif',
            },
          },

        labels: {
  show: true,
  formatter: (value: number) => {
    if (key === 'cpc') {
      return Intl.NumberFormat(locale, {
        style: 'currency',
        currency: currency || 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(value);
    }

    return formatter(value);
  },
  style: {
    colors: '#919EAB',
    fontSize: '11px',
    fontFamily: 'Public Sans, sans-serif',
  },
},
        };
      }
    );


  const axisKeys = AXIS_ORDER
    .map(({ key }) => key)
    .filter((key) =>
      series.some(
        (serie) => axisOf(serie) === key
      )
    );

  const getYAxisIndexForSerie = (
    serie: ChartSeries
  ) => {
    const axisKey = axisOf(serie);

    return axisKeys.indexOf(axisKey);
  };

  /**
   * Formatters du tooltip, dans le même ordre que les séries.
   */
  const seriesFormatters = series.map(
    (serie) =>
      makeFormatter(
        serie.format,
        locale,
        currency
      )
  );

  const colors = series.map(
    (serie) => serie.color
  );

  const MIN_LABEL_SEGMENT_HEIGHT = 10;

  const labelAnnotations: any =
    showLabels
      ? series.flatMap(
        (
          serie,
          seriesIndex
        ) =>
          serie.data
            .map(
              (
                value,
                dataPointIndex
              ) => {
                const numericValue =
                  Number(value);

                if (
                  !Number.isFinite(
                    numericValue
                  ) ||
                  numericValue <= 0
                ) {
                  return null;
                }

                const formatter =
                  makeDisplayFormatter(
                    serie.format,
                    locale,
                    currency
                  );

                const yAxisIndex =
                  getYAxisIndexForSerie(
                    serie
                  );

                return {
                  x: labels[
                    dataPointIndex
                  ],

                  y: numericValue,

                  yAxisIndex,

                  marker: {
                    size: 0,
                    strokeWidth: 0,
                    strokeColor: 'transparent',
                    fillColor: 'transparent',
                  },

                  label: {
                    text:
                      formatter(
                        numericValue
                      ),

                    // exactement le même principe
                    // bar => sommet de barre
                    // line => juste au-dessus de la ligne
                    offsetY:
                      serie.type === 'bar'
                        ? 7
                        : -3,

                    borderColor:
                      'rgba(145, 158, 171, 0.20)',

                    borderWidth: 1,

                    borderRadius: 4,

                    style: {
                      background:
                        '#FFFFFF',

                      color:
                        '#1C252E',

                      fontSize:
                        '11px',

                      fontWeight:
                        500,

                      fontFamily:
                        'Public Sans, sans-serif',

                      cssClass:
                        `chart-data-label chart-data-label-series-${seriesIndex} chart-data-label-point-${dataPointIndex}`,
                    },
                  },
                };
              }
            )
            .filter(Boolean)
      )
      : [];

  const injectSeriesDotsIntoLabels = () => {
    requestAnimationFrame(() => {
      const container =
        chartContainerRef.current;

      if (!container) return;

      container
        .querySelectorAll(
          '.chart-label-dot'
        )
        .forEach(
          (dot) => dot.remove()
        );

      series.forEach(
        (
          serie,
          seriesIndex
        ) => {
          const seriesColor =
            serie.color ||
            '#637381';

          const textElements =
            container.querySelectorAll(
              `text.chart-data-label-series-${seriesIndex}`
            );

          textElements.forEach(
            (node) => {
              const text =
                node as SVGTextElement;

              let rect =
                text.previousElementSibling as SVGRectElement | null;

              if (
                !rect ||
                rect.tagName.toLowerCase() !==
                'rect'
              ) {
                const parent =
                  text.parentElement;

                if (!parent) return;

                const siblings =
                  Array.from(
                    parent.children
                  );

                const textIndex =
                  siblings.indexOf(
                    text
                  );

                for (
                  let i =
                    textIndex - 1;
                  i >= 0;
                  i -= 1
                ) {
                  if (
                    siblings[
                      i
                    ].tagName.toLowerCase() ===
                    'rect'
                  ) {
                    rect =
                      siblings[
                      i
                      ] as SVGRectElement;

                    break;
                  }
                }
              }

              if (!rect) return;

              const pointClass =
                Array.from(
                  text.classList
                ).find(
                  (className) =>
                    className.startsWith(
                      'chart-data-label-point-'
                    )
                );

              const dataPointIndex =
                pointClass
                  ? Number(
                    pointClass.replace(
                      'chart-data-label-point-',
                      ''
                    )
                  )
                  : null;

              /**
               * La règle de hauteur minimale ne concerne que les barres.
               *
               * Les annotations des séries line restent toujours affichées.
               */
              if (
                serie.type === 'bar' &&
                dataPointIndex !== null &&
                Number.isFinite(
                  dataPointIndex
                )
              ) {
                const bar =
                  container.querySelector<SVGPathElement>(
                    `.apexcharts-bar-area[index="${seriesIndex}"][j="${dataPointIndex}"]`
                  ) ??
                  container.querySelectorAll<SVGPathElement>(
                    '.apexcharts-bar-series .apexcharts-series'
                  )[seriesIndex]
                    ?.querySelectorAll<SVGPathElement>(
                      '.apexcharts-bar-area'
                    )[dataPointIndex] ??
                  null;

                if (bar) {
                  const barHeightAttribute =
                    bar.getAttribute(
                      'barHeight'
                    );

                  let segmentHeight =
                    barHeightAttribute !== null
                      ? Number(
                        barHeightAttribute
                      )
                      : Number.NaN;

                  if (
                    !Number.isFinite(
                      segmentHeight
                    )
                  ) {
                    try {
                      segmentHeight =
                        bar.getBBox().height;
                    } catch {
                      segmentHeight =
                        Number.POSITIVE_INFINITY;
                    }
                  }

                  const shouldHide =
                    Number.isFinite(
                      segmentHeight
                    ) &&
                    Math.abs(
                      segmentHeight
                    ) <
                    MIN_LABEL_SEGMENT_HEIGHT;

                  if (shouldHide) {
                    text.setAttribute(
                      'display',
                      'none'
                    );

                    rect.setAttribute(
                      'display',
                      'none'
                    );

                    return;
                  }
                }
              }

              /**
               * Une annotation précédemment masquée doit pouvoir
               * réapparaître après un resize / update du graphique.
               */
              text.removeAttribute(
                'display'
              );

              rect.removeAttribute(
                'display'
              );

              const PADDING_X = 8;
              const PADDING_Y = 4;
              const GAP = 4;

              const DOT_RADIUS = 3;
              const DOT_SIZE =
                DOT_RADIUS * 2;

              let textBBox;

              try {
                textBBox =
                  text.getBBox();
              } catch {
                return;
              }

              const textWidth =
                textBBox.width;

              const textHeight =
                textBBox.height;

              const chipWidth =
                PADDING_X +
                DOT_SIZE +
                GAP +
                textWidth +
                PADDING_X;

              const chipHeight =
                PADDING_Y +
                textHeight +
                PADDING_Y;

              const originalXAttribute =
                rect.getAttribute(
                  'data-original-x'
                );

              const originalYAttribute =
                rect.getAttribute(
                  'data-original-y'
                );

              const originalWidthAttribute =
                rect.getAttribute(
                  'data-original-width'
                );

              const originalHeightAttribute =
                rect.getAttribute(
                  'data-original-height'
                );

              const originalX =
                originalXAttribute !==
                  null
                  ? Number(
                    originalXAttribute
                  )
                  : Number(
                    rect.getAttribute(
                      'x'
                    ) || 0
                  );

              const originalY =
                originalYAttribute !==
                  null
                  ? Number(
                    originalYAttribute
                  )
                  : Number(
                    rect.getAttribute(
                      'y'
                    ) || 0
                  );

              const originalWidth =
                originalWidthAttribute !==
                  null
                  ? Number(
                    originalWidthAttribute
                  )
                  : Number(
                    rect.getAttribute(
                      'width'
                    ) || 0
                  );

              const originalHeight =
                originalHeightAttribute !==
                  null
                  ? Number(
                    originalHeightAttribute
                  )
                  : Number(
                    rect.getAttribute(
                      'height'
                    ) || 0
                  );

              if (
                originalXAttribute ===
                null
              ) {
                rect.setAttribute(
                  'data-original-x',
                  String(
                    originalX
                  )
                );
              }

              if (
                originalYAttribute ===
                null
              ) {
                rect.setAttribute(
                  'data-original-y',
                  String(
                    originalY
                  )
                );
              }

              if (
                originalWidthAttribute ===
                null
              ) {
                rect.setAttribute(
                  'data-original-width',
                  String(
                    originalWidth
                  )
                );
              }

              if (
                originalHeightAttribute ===
                null
              ) {
                rect.setAttribute(
                  'data-original-height',
                  String(
                    originalHeight
                  )
                );
              }

              const centerX =
                originalX +
                originalWidth / 2;

              const centerY =
                originalY +
                originalHeight / 2;

              const chipX =
                centerX -
                chipWidth / 2;

              const chipY =
                centerY -
                chipHeight / 2;

              rect.setAttribute(
                'x',
                String(chipX)
              );

              rect.setAttribute(
                'y',
                String(chipY)
              );

              rect.setAttribute(
                'width',
                String(
                  chipWidth
                )
              );

              rect.setAttribute(
                'height',
                String(
                  chipHeight
                )
              );

              rect.setAttribute(
                'rx',
                '4'
              );

              rect.setAttribute(
                'ry',
                '4'
              );

              const dotCenterX =
                chipX +
                PADDING_X +
                DOT_RADIUS;

              const dotCenterY =
                centerY;

              const textStartX =
                chipX +
                PADDING_X +
                DOT_SIZE +
                GAP;

              text.setAttribute(
                'text-anchor',
                'start'
              );

              text.setAttribute(
                'x',
                String(
                  textStartX
                )
              );

              text.setAttribute(
                'y',
                String(
                  centerY
                )
              );

              text.setAttribute(
                'dominant-baseline',
                'middle'
              );

              const circle =
                document.createElementNS(
                  'http://www.w3.org/2000/svg',
                  'circle'
                );

              circle.setAttribute(
                'class',
                `chart-label-dot chart-label-dot-series-${seriesIndex}`
              );

              circle.setAttribute(
                'cx',
                String(
                  dotCenterX
                )
              );

              circle.setAttribute(
                'cy',
                String(
                  dotCenterY
                )
              );

              circle.setAttribute(
                'r',
                String(
                  DOT_RADIUS
                )
              );

              circle.setAttribute(
                'fill',
                seriesColor
              );

              circle.setAttribute(
                'stroke',
                'none'
              );

              text.parentNode?.insertBefore(
                circle,
                text
              );
            }
          );
        }
      );
    });
  };

  const hasLeftAxis = series.some((serie) => {
    const axis = axisOf(serie);

    return axis === 'traffic' || axis === 'cost';
  });

  const hasRightAxis = series.some((serie) => {
    const axis = axisOf(serie);

    return axis === 'ctr' || axis === 'cpc';
  });

  const chartOptions = useChart({
    chart: {
      stacked: false,
      height: '100%',
      events: {
        mounted: (
          chartContext: any
        ) => {
          onChartMounted?.(
            chartContext
          );

          injectSeriesDotsIntoLabels();
        },

        animationEnd: () => {
          injectSeriesDotsIntoLabels();
        },

        updated: () => {
          injectSeriesDotsIntoLabels();
        },
      },
    },

    plotOptions: {
      bar: {
        columnWidth: '65%',
        borderRadius: 4,
        borderRadiusApplication: 'end',
      },
    },

    /**
     * Affichage dynamique des valeurs sur les séries.
     */
    dataLabels: {
      enabled: false,
    },
    fill: {
      colors,
    },

    colors,

    states: {
      hover: {
        filter: {
          type: 'none',
        },
      },
      active: {
        filter: {
          type: 'none',
        },
      },
    },

    stroke: {
      width: series.map((serie) =>
        serie.type === 'bar' ? 0 : 1.4
      ),
    },

    labels,

    markers: {
      size: 0,
    },

    legend: {
      show: false,
    },

    xaxis: {
      labels: {
        offsetY: 4,
      },
    },

    grid: {
      padding: {
        left: hasLeftAxis ? 0 : 70,
        right: hasRightAxis ? 0 : 70,
        bottom: 8,
      },
    },

    yaxis,

    annotations: {
      points:
        labelAnnotations,
    },

    tooltip: {
      shared: true,
      intersect: false,

      custom({
        series: tooltipSeries,
        dataPointIndex,
        w,
      }: any) {
        if (
          !tooltipSeries ||
          !w.globals.seriesNames ||
          !w.globals.colors
        ) {
          return '';
        }

        const date =
          labels[dataPointIndex] || '';

        const rows = tooltipSeries
          .map(
            (
              tooltipSerie: number[],
              index: number
            ) => {
              const value =
                tooltipSerie[
                dataPointIndex
                ];

              const formattedValue =
                seriesFormatters[index]
                  ? seriesFormatters[index](
                    value
                  )
                  : value;

              return `
                <div
                  style="
                    display:flex;
                    align-items:center;
                    gap:8px;
                    padding-left:4px;
                  "
                >
                  <span
                    style="
                      display:inline-block;
                      width:2px;
                      height:12px;
                      border-radius:2px;
                      background-color:${w.globals.colors[index]};
                      flex-shrink:0;
                    "
                  ></span>

                  <div
                    style="
                      flex:1;
                      display:flex;
                      justify-content:space-between;
                      align-items:center;
                      font-family:Public Sans,sans-serif;
                      font-size:13px;
                      font-weight:500;
                      color:#333c47;
                      white-space:nowrap;
                      gap:16px;
                    "
                  >
                    <span>
                      ${w.globals.seriesNames[index]}
                    </span>

                    <span>
                      ${formattedValue ?? ''}
                    </span>
                  </div>
                </div>
              `;
            }
          )
          .join('');

        return `
          <div
            class="cb-clean-tooltip"
            style="
              background:#fcfcfd;
              border:1px solid rgba(42,50,59,0.2);
              border-radius:12px;
              box-shadow:1px 2px 10px 0 rgba(0,0,0,0.06);
              overflow:hidden;
              min-width:240px;
            "
          >
            <div
              style="
                padding:12px 12px 0 12px;
              "
            >
              <p
                style="
                  font-family:Public Sans,sans-serif;
                  font-size:14px;
                  font-weight:700;
                  color:#333c47;
                  white-space:nowrap;
                  margin:0;
                "
              >
                ${date}
              </p>
            </div>

            <div
              style="
                padding:12px;
                display:flex;
                flex-direction:column;
                gap:6px;
              "
            >
              ${rows}
            </div>
          </div>
        `;
      },
    },
  });

  return (
    <Paper
      sx={{
        backgroundColor: 'common.white',
        boxShadow: 'none',
        border: 'none',
        height: `${CHART_HEIGHT}px`,
        borderRadius: 0,

        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {loading ? (
        <Skeleton
          width="100%"
          height="100%"
          variant="rectangular"
          animation="wave"
          sx={{
            borderRadius: '16px',
          }}
        />
      ) : (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            minHeight: 0,
          }}
        >
          {/* Légende personnalisée */}
          <Stack
            direction="row"
            spacing={1.5}
            justifyContent="flex-end"
            alignItems="center"
          >
            {series.map((serie) => (
              <Stack
                key={serie.key}
                direction="row"
                spacing={0.5}
                alignItems="center"
              >
                <Box
                  sx={{
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    backgroundColor:
                      serie.color,
                    flexShrink: 0,
                  }}
                />

                <Typography
                  sx={{
                    fontSize: '14px',
                    color: '#637381',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {serie.name}
                </Typography>
              </Stack>
            ))}
          </Stack>

          <Box
            ref={chartContainerRef}
            sx={{
              flex: 1,
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
              }}
            >
              <Chart
                key={[
                  showLabels
                    ? 'labels-on'
                    : 'labels-off',
                  matchScale
                    ? 'scale-on'
                    : 'scale-off',
                  filterStore.analysisChartCompareMode,
                  labels.join('|'),
                  ...series.map(
                    (serie) => serie.key
                  ),
                ].join('-')}
                dir="ltr"
                type="line"
                series={series}
                options={chartOptions}
                height="100%"
              />
            </Box>

            {showComparison && (
              <Box
                ref={comparisonContainerRef}
                sx={{
                  position: 'relative',
                  width: '100%',
                  height: `${Math.max(
                    series.length,
                    1
                  ) * 16}px`,
                  mt: '-2px',
                }}
              >
                {comparisonPeriodLabel && (
                  <Box
                    ref={comparisonPeriodRef}
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      transform: 'translateX(-50%)',
                      height: '100%',
                      minWidth: '104px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      pointerEvents: 'none',
                    }}
                  >
                    <Typography
                      sx={{
                        whiteSpace: 'pre-line',
                        textAlign: 'left',
                        fontVariantNumeric:
                          'lining-nums tabular-nums',
                        fontFamily:
                          'Public Sans, sans-serif',
                        fontSize: '12px',
                        fontStyle: 'normal',
                        fontWeight: 400,
                        lineHeight: '120%',
                        color: '#637381',
                      }}
                    >
                      {comparisonPeriodLabel}
                    </Typography>
                  </Box>
                )}

                {comparisonValues.map(
                  (bucketValues, bucketIndex) => (
                    <Stack
                      ref={(element: HTMLDivElement | null) => {
                        comparisonItemRefs.current[
                          bucketIndex
                        ] = element;
                      }}
                      key={`comparison-bucket-${bucketIndex}`}
                      spacing="2px"
                      alignItems="center"
                      sx={{
                        position: 'absolute',
                        top: 0,

                        /**
                         * La valeur de left est injectée par
                         * useLayoutEffect depuis le vrai label X.
                         */
                        left: 0,

                        /**
                         * Le centre du bloc est placé exactement
                         * sur le centre de la date.
                         */
                        transform: 'translateX(-50%)',

                        minWidth: '76px',
                      }}
                    >
                      {bucketValues.map((metric) => {
                        const value = metric.value;

                        const textColor =
                          value === null ||
                            value === 0
                            ? '#919EAB'
                            : value > 0
                              ? '#22A06B'
                              : '#FF5630';

                        return (
                          <Box
                            key={`${bucketIndex}-${metric.metricKey}`}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: '4px',
                              minHeight: '14px',
                            }}
                          >
                            {bucketIndex === 0 && (
                              <Box
                                sx={{
                                  width: '8px',
                                  height: '8px',
                                  borderRadius: '50%',
                                  backgroundColor: metric.color,
                                  flexShrink: 0,
                                  mr: 1
                                }}
                              />
                            )}

                            <Typography
                              sx={{
                                textAlign: 'center',
                                fontVariantNumeric: 'lining-nums tabular-nums ordinal',
                                fontFamily: 'Public Sans',
                                fontSize: '12px',
                                color: textColor,
                                fontStyle: 'normal',
                                fontWeight: 400,
                                lineHeight: '120%', // 14.4px
                              }}
                            >
                              {formatComparisonValue(
                                value
                              )}
                            </Typography>
                          </Box>
                        );
                      })}
                    </Stack>
                  )
                )}
              </Box>
            )}
          </Box>

        </Box>
      )}
    </Paper>
  );
}